import {
  Alert,
  Anchor,
  Button,
  Group,
  Stack,
  Stepper,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconBriefcase, IconId, IconShieldCheck } from "@tabler/icons-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { DocumentCapture } from "../components/DocumentCapture";
import { EmailVerificationCard } from "../components/EmailVerificationCard";
import { OnboardingSection, OnboardingShell } from "../components/OnboardingShell";
import { PhoneVerificationCard } from "../components/PhoneVerificationCard";
import { ResumeParseSection } from "../components/ResumeParseSection";
import { LoadingOverlay } from "../components/ui";
import { useAuth } from "../context/useAuth";
import { isIdentityVerified } from "../lib/accountVerification";
import { candidatesApi, recruiterApi } from "../lib/api";
import { ApiError } from "../lib/apiClient";
import {
  IDENTITY_KYC_PAGE,
  IDENTITY_KYC_REQUIRED_SLOTS,
  IDENTITY_KYC_SLOT_LABELS,
  KYC_REVIEW_MISSING,
  KYC_REVIEW_READY,
  identityKycPackEditable,
  kycStatusLabel,
} from "../lib/kyc";
import { validateResumeFile } from "../lib/extractResumeText";
import { canAccessKycStep } from "../lib/recruiterOnboarding";

const SLOT_LABELS = IDENTITY_KYC_SLOT_LABELS;

export function KycPage() {
  const { user } = useAuth();
  const isAffiliate = user?.role === "affiliate";
  const queryClient = useQueryClient();
  const kycQuery = useQuery({ queryKey: ["my-kyc"], queryFn: recruiterApi.kycStatus });
  const [step, setStep] = useState(0);
  const [aadhaar, setAadhaar] = useState("");
  const [pan, setPan] = useState("");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [resumeId, setResumeId] = useState<string | null>(null);
  const [resumeBusy, setResumeBusy] = useState(false);
  const [resumeError, setResumeError] = useState<string | null>(null);
  const [uploadingSlot, setUploadingSlot] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const kyc = kycQuery.data;
  const steps = useMemo(
    () =>
      isAffiliate
        ? ["Account verification", "Selfie", "Aadhaar", "PAN", "Submit"]
        : ["Account verification", "Selfie", "Aadhaar", "PAN", "Resume", "Submit"],
    [isAffiliate],
  );
  const identityVerified = isIdentityVerified(user);

  const uploadMutation = useMutation({
    mutationFn: ({ slot, file }: { slot: string; file: File }) => recruiterApi.uploadKycFile(slot, file),
    onSuccess: (data) => {
      queryClient.setQueryData(["my-kyc"], data);
      queryClient.invalidateQueries({ queryKey: ["my-profile"] });
    },
    onError: (err) => {
      notifications.show({
        color: "red",
        title: "Upload failed",
        message: err instanceof ApiError ? err.message : "Could not upload that file",
      });
    },
    onSettled: () => setUploadingSlot(null),
  });

  const submitMutation = useMutation({
    mutationFn: () =>
      recruiterApi.submitKyc({
        aadhaar_number: aadhaar.replace(/\s/g, "") || undefined,
        pan: pan.trim().toUpperCase() || undefined,
        resume_id: resumeId ?? undefined,
      }),
    onSuccess: (data) => {
      setSubmitError(null);
      queryClient.setQueryData(["my-kyc"], data);
      queryClient.invalidateQueries({ queryKey: ["my-profile"] });
      notifications.show({
        color: "green",
        title: data.kyc_status === "verified" ? "Documents saved" : "KYC submitted",
        message:
          data.kyc_status === "verified"
            ? "Your ID pack is on file. Marketplace access is unchanged."
            : "An admin will review your documents. You can browse jobs while you wait.",
      });
    },
    onError: (err) => {
      const message = err instanceof ApiError ? err.message : "Check the required fields and try again";
      setSubmitError(message);
      notifications.show({
        color: "red",
        title: IDENTITY_KYC_PAGE.submitErrorTitle,
        message,
      });
    },
  });

  async function onSlotFile(slot: string, file: File) {
    setUploadingSlot(slot);
    await uploadMutation.mutateAsync({ slot, file });
  }

  async function handleResume(file: File | null) {
    setResumeFile(file);
    setResumeId(null);
    setResumeError(null);
    if (!file) return;
    const invalid = validateResumeFile(file);
    if (invalid) {
      setResumeError(invalid);
      return;
    }
    setResumeBusy(true);
    try {
      const uploaded = await candidatesApi.uploadResume(file);
      setResumeId(uploaded.resume_id);
    } catch (err) {
      setResumeError(err instanceof ApiError ? err.message : "Could not upload resume");
    } finally {
      setResumeBusy(false);
    }
  }

  if (kycQuery.isLoading) {
    return <LoadingOverlay height="var(--axio-unit-360)" />;
  }

  const waiting = kyc?.kyc_status === "submitted";
  const verifiedComplete = kyc?.kyc_status === "verified" && kyc.kyc_documents_complete;
  const verifiedRecovery = kyc?.kyc_status === "verified" && !kyc.kyc_documents_complete;
  const editable = identityKycPackEditable(kyc?.kyc_status, kyc?.kyc_documents_complete);
  const filled = new Set(kyc?.filled_slots ?? []);
  const aadhaarReady = Boolean(aadhaar.replace(/\s/g, "") || kyc?.aadhaar_last4);
  const panReady = Boolean(pan.trim() || kyc?.pan_set);
  const resumeReady = Boolean(kyc?.has_resume || resumeId);
  const reviewItems = [
    ...IDENTITY_KYC_REQUIRED_SLOTS.map((slot) => ({
      key: slot,
      label: SLOT_LABELS[slot],
      ready: filled.has(slot),
    })),
    { key: "aadhaar_number", label: SLOT_LABELS.aadhaar_number, ready: aadhaarReady },
    { key: "pan", label: SLOT_LABELS.pan, ready: panReady },
    ...(!isAffiliate ? [{ key: "resume", label: SLOT_LABELS.resume, ready: resumeReady }] : []),
  ];
  const reviewComplete = reviewItems.every((item) => item.ready);

  return (
    <OnboardingShell
      role={isAffiliate ? "affiliate" : "recruiter"}
      accent="violet"
      centerPanel
      heroTitle={
        <>
          Verify yourself,
          <br />
          <span className="gradientText">
            {isAffiliate ? IDENTITY_KYC_PAGE.affiliateHero : IDENTITY_KYC_PAGE.recruiterHero}
          </span>
        </>
      }
      heroSubtitle={isAffiliate ? IDENTITY_KYC_PAGE.affiliateSubtitle : IDENTITY_KYC_PAGE.recruiterSubtitle}
      statLabel="After approval"
      statValue="Full JD"
      highlights={[
        {
          icon: IconId,
          text: isAffiliate ? IDENTITY_KYC_PAGE.affiliateHighlightDocs : IDENTITY_KYC_PAGE.recruiterHighlightResume,
        },
        { icon: IconBriefcase, text: IDENTITY_KYC_PAGE.browseBeforeApproval },
        {
          icon: IconShieldCheck,
          text: isAffiliate ? IDENTITY_KYC_PAGE.affiliateUnlock : IDENTITY_KYC_PAGE.recruiterUnlock,
        },
      ]}
      footer={
        <>
          <Anchor component={Link} to={isAffiliate ? "/affiliate/jobs" : "/jobs"} fw="var(--axio-font-weight-semibold)" c="violet.7">
            Browse live jobs
          </Anchor>
          {" · "}
          <Anchor component={Link} to={isAffiliate ? "/affiliate" : "/"} c="dimmed">
            {isAffiliate ? IDENTITY_KYC_PAGE.affiliateHome : IDENTITY_KYC_PAGE.recruiterHome}
          </Anchor>
        </>
      }
    >
      <Stack gap="lg">
        <div>
          <Title order={3} fw="var(--axio-font-weight-extra-bold)" style={{ fontFamily: "var(--axio-font-head)" }}>
            {isAffiliate ? IDENTITY_KYC_PAGE.affiliateTitle : IDENTITY_KYC_PAGE.recruiterTitle}
          </Title>
          <Text size="sm" c="dimmed" mt="var(--axio-unit-6)">
            Status: {kycStatusLabel(kyc?.kyc_status, kyc?.kyc_documents_complete)}
          </Text>
        </div>

        {kyc?.kyc_status === "rejected" && (
          <Alert color="red" title="Replace the files that failed">
            {kyc.kyc_notes || "An admin rejected this pack. Update the documents and resubmit."}
          </Alert>
        )}

        {kyc?.kyc_status === "verified" && !kyc.kyc_documents_complete && (
          <Alert color="blue" title={IDENTITY_KYC_PAGE.legacyVerifiedRecoveryTitle}>
            {IDENTITY_KYC_PAGE.legacyVerifiedRecoveryBody}
          </Alert>
        )}

        {verifiedComplete ? (
          <Alert color="green" title="KYC complete">
            {isAffiliate ? IDENTITY_KYC_PAGE.verifiedAffiliateBody : IDENTITY_KYC_PAGE.verifiedBody}
            <Button component={Link} to={isAffiliate ? "/affiliate/jobs" : "/jobs"} mt="sm" color="green" variant="light">
              Browse jobs
            </Button>
          </Alert>
        ) : waiting ? (
          <Alert color="violet" title="Pack submitted">
            {IDENTITY_KYC_PAGE.waitingBody}
            <Group mt="sm">
              <Button component={Link} to={isAffiliate ? "/affiliate/jobs" : "/jobs"} color="violet">
                Browse live jobs
              </Button>
            </Group>
          </Alert>
        ) : null}

        {editable && (
          <>
            <Stepper
              active={step}
              onStepClick={(next) => {
                if (canAccessKycStep(next, identityVerified)) setStep(next);
              }}
              size="sm"
              allowNextStepsSelect={identityVerified}
            >
              {steps.map((label) => (
                <Stepper.Step key={label} label={label} />
              ))}
            </Stepper>

            {steps[step] === "Account verification" && (
              <OnboardingSection
                title="Verify your contact details"
                description="Complete this once so there are no surprises when you start submitting candidates."
              >
                <Stack gap="md">
                  <EmailVerificationCard />
                  <PhoneVerificationCard />
                  {identityVerified && (
                    <Alert color="green" title="Account verified">
                      Your email and phone are verified. Continue to your identity documents.
                    </Alert>
                  )}
                </Stack>
              </OnboardingSection>
            )}

            {steps[step] === "Selfie" && (
              <OnboardingSection title="Live photo" description="Face the camera. Upload is fine if the camera is blocked.">
                <DocumentCapture
                  label={SLOT_LABELS.selfie}
                  facing="user"
                  filled={filled.has("selfie")}
                  uploading={uploadingSlot === "selfie"}
                  onFile={(file) => onSlotFile("selfie", file)}
                />
              </OnboardingSection>
            )}

            {steps[step] === "Aadhaar" && (
              <OnboardingSection
                title="Aadhaar"
                description="Front and back. We store last 4 and a hash, not the full number."
              >
                <Stack>
                  <DocumentCapture
                    label={SLOT_LABELS.aadhaar_front}
                    facing="environment"
                    filled={filled.has("aadhaar_front")}
                    uploading={uploadingSlot === "aadhaar_front"}
                    onFile={(file) => onSlotFile("aadhaar_front", file)}
                  />
                  <DocumentCapture
                    label={SLOT_LABELS.aadhaar_back}
                    facing="environment"
                    filled={filled.has("aadhaar_back")}
                    uploading={uploadingSlot === "aadhaar_back"}
                    onFile={(file) => onSlotFile("aadhaar_back", file)}
                  />
                  <TextInput
                    label="12-digit Aadhaar"
                    placeholder="XXXX XXXX 1234"
                    value={aadhaar}
                    onChange={(e) => setAadhaar(e.currentTarget.value.replace(/[^\d\s]/g, ""))}
                    description={kyc?.aadhaar_last4 ? `On file: last 4 ${kyc.aadhaar_last4}` : undefined}
                  />
                </Stack>
              </OnboardingSection>
            )}

            {steps[step] === "PAN" && (
              <OnboardingSection title="PAN" description="Front and the reverse if your card has a QR.">
                <Stack>
                  <DocumentCapture
                    label={SLOT_LABELS.pan_front}
                    facing="environment"
                    filled={filled.has("pan_front")}
                    uploading={uploadingSlot === "pan_front"}
                    onFile={(file) => onSlotFile("pan_front", file)}
                  />
                  <DocumentCapture
                    label={SLOT_LABELS.pan_back}
                    facing="environment"
                    filled={filled.has("pan_back")}
                    uploading={uploadingSlot === "pan_back"}
                    onFile={(file) => onSlotFile("pan_back", file)}
                  />
                  <TextInput
                    label="PAN"
                    placeholder="ABCDE1234F"
                    value={pan}
                    onChange={(e) => setPan(e.currentTarget.value.toUpperCase())}
                    description={kyc?.pan_set ? "PAN is already on file. Re-enter only if you need to change it." : undefined}
                  />
                </Stack>
              </OnboardingSection>
            )}

            {steps[step] === "Resume" && (
              <OnboardingSection title="Recruiter resume" description="Your resume is required before KYC can be submitted.">
                {kyc?.has_resume && (
                  <Alert color="green" mb="md" title="Resume on file">
                    Your recruiter resume is ready. Upload another file only if you want to replace it.
                  </Alert>
                )}
                <ResumeParseSection
                  file={resumeFile}
                  busy={resumeBusy}
                  error={resumeError}
                  hint={resumeId ? "Resume saved." : null}
                  title="Your resume"
                  description="Upload a current PDF or DOCX recruiter resume."
                  onFileChange={(file) => void handleResume(file)}
                />
              </OnboardingSection>
            )}

            {steps[step] === "Submit" && (
              <OnboardingSection
                title="Review"
                description={
                  verifiedRecovery
                    ? IDENTITY_KYC_PAGE.legacyVerifiedReviewDescription
                    : isAffiliate
                    ? "All contact verification and identity documents are required."
                    : "All contact verification, identity documents, and your resume are required."
                }
              >
                <Stack gap="xs">
                  <Text size="sm">Email and phone: {identityVerified ? "Verified" : KYC_REVIEW_MISSING}</Text>
                  {reviewItems.map((item) => (
                    <Text key={item.key} size="sm">
                      {item.label}: {item.ready ? KYC_REVIEW_READY : KYC_REVIEW_MISSING}
                    </Text>
                  ))}
                  {submitError ? (
                    <Alert color="red" title={IDENTITY_KYC_PAGE.submitErrorTitle}>
                      {submitError}
                    </Alert>
                  ) : null}
                  <Button
                    color="violet"
                    loading={submitMutation.isPending}
                    disabled={!identityVerified || !reviewComplete}
                    onClick={() => submitMutation.mutate()}
                  >
                    {verifiedRecovery ? IDENTITY_KYC_PAGE.legacyVerifiedSubmitLabel : "Submit for review"}
                  </Button>
                </Stack>
              </OnboardingSection>
            )}

            <Group justify="space-between">
              <Button variant="default" disabled={step === 0} onClick={() => setStep((s) => Math.max(0, s - 1))}>
                Back
              </Button>
              {step < steps.length - 1 && (
                <Button
                  color="violet"
                  disabled={step === 0 && !identityVerified}
                  onClick={() => setStep((s) => Math.min(steps.length - 1, s + 1))}
                >
                  Next
                </Button>
              )}
            </Group>
          </>
        )}
      </Stack>
    </OnboardingShell>
  );
}
